import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import CTAButton from '../components/CTAButton';
import TrustSignals from '../components/TrustSignals';
import ComparisonSection from '../components/ComparisonSection';
import TestimonialsCarousel from '../components/TestimonialsCarousel';
import * as FiIcons from 'react-icons/fi';

const { FiClock, FiDollarSign, FiShield, FiArrowRight, FiSmartphone, FiZap, FiCheckCircle } = FiIcons;

const HomePage = () => {
  const benefits = [
    {
      icon: FiZap,
      title: "Instant Online Quotes",
      description: "Get your quote in under 3 minutes, 100% online - no phone calls or meetings required."
    },
    {
      icon: FiSmartphone,
      title: "Buy Coverage Same Day",
      description: "Purchase and download your policy instantly - no waiting, no paperwork, no agent needed."
    },
    {
      icon: FiDollarSign,
      title: "Transparent Pricing",
      description: "See exact costs upfront starting at $19/month - no hidden fees or surprises."
    }
  ];

  const products = [
    {
      title: "General Liability",
      description: "Covers customer injuries and property damage.",
      link: "/general-liability"
    },
    {
      title: "Business Owner's Policy",
      description: "Combines general liability and property coverage.",
      link: "/business-owners-policy"
    },
    {
      title: "Workers' Compensation",
      description: "Covers workplace injuries and medical costs.",
      link: "/workers-compensation"
    },
    {
      title: "Professional Liability",
      description: "Protects against professional mistakes and negligence.",
      link: "/general-liability"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 to-primary-800 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-60"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80)'
          }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex items-center justify-center space-x-4 mb-6"
            >
              <div className="bg-white/20 px-4 py-2 rounded-full backdrop-blur-sm">
                <span className="text-sm font-semibold">100% ONLINE • NO AGENTS • INSTANT COVERAGE</span>
              </div>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-6xl font-bold mb-6"
            >
              Skip the Agent, Buy Online in Minutes
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto"
            >
              Get instant business insurance quotes from Next Insurance. No phone calls, no meetings, no waiting - just fast, affordable coverage for California small businesses.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <CTAButton text="Get Instant Quote - 100% Online" size="xl" className="mb-8" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <TrustSignals className="text-white" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Online Advantages Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Buy Insurance Online?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Skip the traditional insurance agency hassle. Get coverage faster, easier, and on your schedule.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="text-center p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={benefit.icon} className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Comparison Section */}
      <ComparisonSection />

      {/* Product Highlights */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Coverage Available Online
            </h2>
            <p className="text-xl text-gray-600">
              All policies available for instant purchase - no agent required
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product, index) => (
              <motion.div
                key={product.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-3">{product.title}</h3>
                <p className="text-gray-600 mb-4">{product.description}</p>
                <Link
                  to={product.link}
                  className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
                >
                  Get Instant Quote
                  <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Testimonials Carousel */}
      <TestimonialsCarousel />

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-4xl font-bold mb-6"
          >
            Ready to Skip the Agent and Buy Online?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl mb-8"
          >
            Join thousands who chose the faster, easier way to get business insurance. Get your instant quote and buy coverage in minutes.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <CTAButton text="Get Instant Quote - No Agent Needed" size="xl" className="bg-white text-primary-600 hover:bg-gray-100" />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;